
/**
 * 未来属性カードのトリガー実装
 *
 * このファイルには未来属性カードの個別トリガー効果を実装します。
 * 各カードは独自のトリガー定義を持ち、triggerEngineによって管理されます。
 */

import { TRIGGER_TYPES, ACTIVATION_TYPES } from '../triggerTypes';
import type { TriggerContext } from '../triggerEngine';
import type { CardData, CardWithId, MonsterInstance } from '../../types/card';
import type { TriggerDefinition } from '../../types/effect';
import {
  getPlayerContext,
  millDeck,
  millOpponentDeck,
  conditionalDamage,
  searchCard,
  reviveFromGraveyard,
  drawCards,
  healLife,
  modifyAttack,
  applyStatusToAllOpponentMonsters,
} from '../effectHelpers';
import { STATUS_EFFECT_TYPES, statusEffectEngine } from '../statusEffects';

/**
 * 未来属性カードのトリガー定義
 * カードID => トリガー配列のマッピング
 */
export const futureCardTriggers = {
  /**
   * C0000085: 胡乱なドローン
   * 【召喚時】デッキの上から1枚を墓地に送る。
   * そのカードが未来属性の場合、次のターン終了時までこのカードの攻撃力を600に変更。
   */
  C0000085: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'デッキから1枚を墓地に送る',
      effect: (context: TriggerContext) => {
        const { addLog, monsterIndex } = context;
        const { myField, setMyField } = getPlayerContext(context);
        const milledCards = millDeck(context, 1);

        if (milledCards.length > 0) {
          const card = milledCards[0];
          addLog(`${card.name}を墓地に送った`, 'info');

          // 未来属性の場合、攻撃力を600に変更
          if (card.attribute === '未来') {
            if (monsterIndex !== undefined && myField[monsterIndex]) {
              setMyField((prev) => {
                const newField = [...prev];
                newField[monsterIndex] = {
                  ...newField[monsterIndex],
                  currentAttack: 600,
                  attackModified: true, // フラグを立てる
                } as any;
                return newField;
              });
              addLog(`胡乱なドローンの攻撃力が600になった！`, 'info');
            }
          }
        }
      },
    },
  ],

  /**
   * C0000105: 魔導炉の残骸獣
   * 【召喚時】自分の墓地の未来属性カード1枚をデッキに戻す。
   * その後、相手プレイヤーに300ダメージを与える。
   */
  C0000105: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '墓地の未来属性カードをデッキに戻し300ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myGraveyard, setMyGraveyard, myDeck, setMyDeck } = getPlayerContext(context);

        // 墓地から未来属性カードを探す
        const futureCard = myGraveyard.find((card) => card.attribute === '未来');

        if (futureCard) {
          // 墓地から削除
          setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== futureCard.uniqueId));
          // デッキに追加
          setMyDeck((prev) => [...prev, futureCard]);
          addLog(`${futureCard.name}をデッキに戻した`, 'info');

          // 相手に300ダメージ
          conditionalDamage(context as any, 300, 'opponent');
        } else {
          addLog('墓地に未来属性カードがない', 'info');
        }
      },
    },
  ],

  /**
   * C0000106: 次元渡り・ヴォイドウォーカー
   * 【自分エンドフェイズ時】レスト状態のSPトークン1個をアクティブにする（ターンに1度）。
   */
  C0000106: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'レストSP1個をアクティブに（1ターンに1度）',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myRestedSP, setMyActiveSP, setMyRestedSP } = getPlayerContext(context);

        if (myRestedSP > 0) {
          setMyRestedSP((prev) => prev - 1);
          setMyActiveSP((prev) => prev + 1);
          addLog('次元渡り・ヴォイドウォーカーの効果: SP1個をアクティブにした', 'info');
        }
      },
    },
  ],

  /**
   * C0000107: 守護の魔導ゴーレム
   * 【自分エンドフェイズ時】自分の未来属性モンスターのHPを500回復（ターンに1度）。
   */
  C0000107: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '未来属性モンスターのHP500回復（1ターンに1度）',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { setMyField } = getPlayerContext(context);

        let healed = false;

        setMyField((prev) => {
          const newField = prev.map((monster) => {
            if (monster && monster.attribute === '未来') {
              const maxHp = (monster as any).maxHp ?? monster.hp;
              const newHp = Math.min(monster.currentHp + 500, maxHp);
              if (newHp > monster.currentHp) {
                healed = true;
                addLog(`${monster.name}のHPが500回復した`, 'heal');
              }
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
          return newField;
        });

        if (!healed) {
          addLog('回復対象の未来属性モンスターがいない', 'info');
        }
      },
    },
  ],

  /**
   * C0000116: 時空旅人ソラリア
   * 【召喚時】1枚ドローする。その後、手札を1枚デッキの下に戻す。
   */
  C0000116: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '1枚ドロー後、手札1枚をデッキの下に戻す',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingHandSelection } = context;
        const { myHand, setMyDeck, setMyHand } = getPlayerContext(context);

        // 1枚ドロー
        const drawnCards = drawCards(context, 1);

        if (drawnCards.length > 0) {
          addLog(`${drawnCards[0].name}をドローした`, 'info');

          // ドロー後の手札が1枚以上あれば選択させる
          if (myHand.length + drawnCards.length > 0) {
            addLog('デッキの下に戻すカードを手札から選択してください', 'info');

            // 手札選択モードに入る
            setPendingHandSelection!({
              message: 'デッキの下に戻すカードを選択',
              callback: (selectedCards: CardWithId[]) => {
                // callbackは配列を受け取る
                if (selectedCards && selectedCards.length > 0) {
                  const selectedCard = selectedCards[0];
                  // 手札から削除してデッキの下に追加
                  setMyHand((prev) => prev.filter((c) => c.uniqueId !== selectedCard.uniqueId) as CardWithId[]);
                  setMyDeck((prev) => [...prev, selectedCard] as CardWithId[]);
                  addLog(`${selectedCard.name}をデッキの下に戻した`, 'info');
                }
              },
              selectionOwner: context.currentPlayer,
            } as any);
          }
        }
      },
    },
  ],

  /**
   * C0000183: 殲滅の魔導ゴーレム
   * 【召喚時】相手フィールド全体に600ダメージを与える。
   */
  C0000183: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手フィールド全体に600ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        let damaged = false;
        const destroyedMonsters: MonsterInstance[] = [];

        setOpponentField((prev) => {
          return prev.map((monster) => {
            if (monster) {
              damaged = true;
              const newHp = monster.currentHp - 600;
              addLog(`${monster.name}に600ダメージ！`, 'damage');

              if (newHp <= 0) {
                destroyedMonsters.push(monster);
                addLog(`${monster.name}は破壊された！`, 'damage');
                return null;
              }
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        // 破壊されたモンスターを墓地に送る
        if (destroyedMonsters.length > 0) {
          setOpponentGraveyard((prev) => [...prev, ...destroyedMonsters]);
        }

        if (!damaged) {
          addLog('相手フィールドにモンスターがいない', 'info');
        }
      },
    },
  ],

  /**
   * C0000249: ミランの使い魔未来鴉
   * 【召喚時】デッキの上から3枚を見て、好きな順番で戻す。
   */
  C0000249: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'デッキの上から3枚を確認',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview } = context;
        const { myDeck, setMyDeck } = getPlayerContext(context);

        if (myDeck.length === 0) {
          addLog('デッキにカードがありません', 'info');
          return;
        }

        const cardCount = Math.min(3, myDeck.length);
        const topCards = myDeck.slice(0, cardCount);
        addLog(`ミランの使い魔未来鴉の効果: デッキの上${cardCount}枚を確認`, 'info');

        // デッキ確認モーダルを表示
        setPendingDeckReview!({
          cards: topCards,
          title: 'ミランの使い魔未来鴉',
          message: 'ドラッグして順番を変更し、確定してください',
          allowReorder: true,
          onConfirm: (reorderedCards: CardWithId[]) => {
            // 並び替えた順番でデッキを更新
            setMyDeck((prev) => [...reorderedCards, ...prev.slice(cardCount)] as CardWithId[]);
            addLog(`カードをデッキの上に戻した: ${reorderedCards.map((c: CardWithId) => c.name).join(' → ')}`, 'info');
          },
          selectionOwner: context.currentPlayer,
        } as any);
      },
    },
  ],

  /**
   * C0000250: 時空渡りのカモメ
   * 【召喚時】相手の手札を1枚ランダムに墓地に送る。
   */
  C0000250: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手の手札を1枚ランダムに墓地に送る',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentHand, setOpponentHand, setOpponentGraveyard } = getPlayerContext(context);

        if (opponentHand.length > 0) {
          // ランダムに1枚選択
          const randomIndex = Math.floor(Math.random() * opponentHand.length);
          const discardedCard = opponentHand[randomIndex];

          // 手札から削除して墓地に送る
          setOpponentHand((prev) => prev.filter((c, idx) => idx !== randomIndex));
          setOpponentGraveyard((prev) => [...prev, discardedCard]);

          addLog(`相手の${discardedCard.name}を墓地に送った`, 'info');
        } else {
          addLog('相手の手札がない', 'info');
        }
      },
    },
  ],

  /**
   * C0000251: 紫鴉の魔導士ミラン
   * 【召喚時】デッキから未来属性魔法カード1枚を手札に加える。
   */
  C0000251: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'デッキから未来属性魔法カードをサーチ',
      effect: (context: TriggerContext) => {
        const foundCard = searchCard(context, (card) => {
          return card.attribute === '未来' && card.type === 'magic';
        });

        if (foundCard) {
          context.addLog(`${foundCard.name}を手札に加えた`, 'info');
        } else {
          context.addLog('未来属性魔法カードが見つからない', 'info');
        }
      },
    },
  ],

  /**
   * C0000252: 未来鴉の群れ
   * 【自壊時】デッキから「未来属性」モンスター1体を墓地に送り、1枚ドローする。
   */
  C0000252: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '未来属性モンスターを墓地に送り1枚ドロー',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myDeck, setMyDeck, setMyGraveyard } = getPlayerContext(context);

        // デッキから未来属性モンスターを探す
        const futureMonsterIndex = myDeck.findIndex(
          (card) => card.attribute === '未来' && card.type === 'monster'
        );

        if (futureMonsterIndex !== -1) {
          const futureMonster = myDeck[futureMonsterIndex];

          // デッキから削除して墓地に送る
          setMyDeck((prev) => prev.filter((c, idx) => idx !== futureMonsterIndex));
          setMyGraveyard((prev) => [...prev, futureMonster]);
          addLog(`${futureMonster.name}を墓地に送った`, 'info');

          // 1枚ドロー
          drawCards(context, 1);
        } else {
          addLog('デッキに未来属性モンスターがない', 'info');
        }
      },
    },
  ],

  /**
   * C0000256: 紫時空の幻鴉
   * 【召喚時】自分の墓地の「未来属性」カード1枚を手札に加える。
   */
  C0000256: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '墓地の未来属性カードを手札に加える',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myGraveyard, setMyGraveyard, setMyHand } = getPlayerContext(context);

        // 墓地から未来属性カードを探す
        const futureCard = myGraveyard.find((card) => card.attribute === '未来');

        if (futureCard) {
          // 墓地から削除して手札に加える
          setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== futureCard.uniqueId));
          setMyHand((prev) => [...prev, futureCard]);
          addLog(`${futureCard.name}を手札に加えた`, 'info');
        } else {
          addLog('墓地に未来属性カードがない', 'info');
        }
      },
    },
  ],

  /**
   * C0000257: 時読みの塔 (フィールドカード)
   * 【常時】「未来属性」モンスターの攻撃力を400アップ。
   * 【エンドフェイズ時】相手のデッキの1番上のカードを確認。
   */
  C0000257: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '未来属性モンスターの攻撃力+400',
      effect: (context: TriggerContext) => {
        // 常時効果は別途実装が必要
        // フィールドカードが場にある間、継続的に効果を適用する仕組みが必要
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手のデッキトップを確認',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentDeck } = getPlayerContext(context);

        if (opponentDeck.length > 0) {
          const topCard = opponentDeck[0];
          addLog(`相手のデッキトップ: ${topCard.name}`, 'info');
          // TODO: UIで「デッキの下に送るか、そのまま戻すか」を選択
        } else {
          addLog('相手のデッキがない', 'info');
        }
      },
    },
  ],

  /**
   * C0000278: 灯守の少女
   * 【召喚時】自分のデッキ上1枚を見て、それが「未来属性」カードなら手札に、違えばデッキ下に。
   * 【場を離れる時】自分の墓地の「未来属性」カード1枚をデッキに戻す。
   */
  C0000278: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'デッキ上1枚を確認、未来属性なら手札に、違えばデッキ下に',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

        if (myDeck.length === 0) {
          addLog('デッキにカードがありません', 'info');
          return;
        }

        // デッキトップ1枚を確認
        const topCard = myDeck[0];
        addLog(`デッキトップを確認: ${topCard.name}`, 'info');

        if (topCard.attribute === '未来') {
          // 未来属性なら手札に加える
          setMyDeck((prev) => prev.slice(1));
          setMyHand((prev) => [...prev, topCard]);
          addLog(`${topCard.name}は未来属性！手札に加えた`, 'info');
        } else {
          // 未来属性でなければデッキの下に戻す
          setMyDeck((prev) => {
            const newDeck = prev.slice(1);
            return [...newDeck, topCard];
          });
          addLog(`${topCard.name}は未来属性ではない。デッキの下に戻した`, 'info');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_LEAVE_FIELD,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '場を離れる時、墓地の未来属性カード1枚をデッキに戻す',
      effect: (context: TriggerContext) => {
        const { addLog, leavingCard } = context;
        const { myGraveyard, setMyGraveyard, setMyDeck } = getPlayerContext(context);

        // 墓地から未来属性カードを探す（自分自身は除外）
        const leavingCardAny = leavingCard as any;
        const futureCard = myGraveyard.find(
          (card) => card.attribute === '未来' && card.uniqueId !== leavingCardAny?.uniqueId
        );

        if (futureCard) {
          // 墓地から削除してデッキに戻す
          setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== futureCard.uniqueId));
          setMyDeck((prev) => [...prev, futureCard]);
          addLog(`灯守の少女の効果: ${futureCard.name}を墓地からデッキに戻した`, 'info');
        } else {
          addLog('墓地に未来属性カードがない', 'info');
        }
      },
    },
  ],

  /**
   * C0000267: 星翼の飛舟エクラオン
   * 【召喚時】自分のデッキ上3枚を見て、未来モンスター1枚を手札に加え、残りを好きな順番で戻す。
   * 場を離れる時、相手プレイヤーに600ダメージ。
   */
  C0000267: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'デッキ上3枚を見て、未来モンスター1枚を手札に加える',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview } = context;
        const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

        if (myDeck.length === 0) {
          addLog('デッキにカードがありません', 'info');
          return;
        }

        // デッキ上3枚を確認
        const cardCount = Math.min(3, myDeck.length);
        const topCards = myDeck.slice(0, cardCount);
        addLog(`星翼の飛舟エクラオンの効果: デッキ上${cardCount}枚を確認`, 'info');

        // 未来属性モンスターを探す
        const futureMonsters = topCards.filter(
          (card) => card.attribute === '未来' && card.type === 'monster'
        );

        if (futureMonsters.length === 0) {
          // 未来モンスターがいない場合、並び替えて戻す
          setPendingDeckReview!({
            cards: topCards,
            title: '星翼の飛舟エクラオン',
            message: '未来属性モンスターがいませんでした。カードの順番を変更してデッキに戻してください',
            allowReorder: true,
            onConfirm: (reorderedCards: CardWithId[]) => {
              setMyDeck((prev) => [...reorderedCards, ...prev.slice(cardCount)] as CardWithId[]);
              addLog(`カードをデッキに戻した: ${reorderedCards.map((c: CardWithId) => c.name).join(' → ')}`, 'info');
            },
            selectionOwner: context.currentPlayer,
          } as any);
        } else if (futureMonsters.length === 1) {
          // 未来モンスターが1体の場合、自動で手札に加え、残りを並び替え
          const futureMonster = futureMonsters[0];
          const remaining = topCards.filter((c) => c.uniqueId !== futureMonster.uniqueId);

          setMyHand((prev) => [...prev, futureMonster]);
          addLog(`${futureMonster.name}を手札に加えた`, 'info');

          if (remaining.length > 1) {
            // 残りが2枚以上なら並び替え
            setPendingDeckReview!({
              cards: remaining,
              title: '星翼の飛舟エクラオン',
              message: '残りのカードの順番を変更してデッキに戻してください',
              allowReorder: true,
              onConfirm: (reorderedCards: CardWithId[]) => {
                setMyDeck((prev) => [...reorderedCards, ...prev.slice(cardCount)] as CardWithId[]);
                addLog(`残りをデッキに戻した: ${reorderedCards.map((c: CardWithId) => c.name).join(' → ')}`, 'info');
              },
              selectionOwner: context.currentPlayer,
            } as any);
          } else if (remaining.length === 1) {
            // 残りが1枚なら自動で戻す
            setMyDeck((prev) => [remaining[0], ...prev.slice(cardCount)]);
            addLog(`${remaining[0].name}をデッキに戻した`, 'info');
          } else {
            // 残りがない
            setMyDeck((prev) => prev.slice(cardCount));
          }
        } else {
          // 未来モンスターが複数の場合、選択させる
          setPendingDeckReview!({
            cards: topCards,
            title: '星翼の飛舟エクラオン',
            message: '手札に加える未来属性モンスターを1枚選択してください',
            allowReorder: false,
            selectMode: {
              enabled: true,
              count: 1,
              filter: (card: CardWithId) => card.attribute === '未来' && card.type === 'monster',
            },
            onSelect: (selectedCards: CardWithId[], remainingCards: CardWithId[]) => {
              // selectedCardsは常に配列（DeckReviewModalの仕様）
              const selectedMonster = selectedCards[0];
              if (!selectedMonster) return;
              setMyHand((prev) => [...prev, selectedMonster]);
              addLog(`${selectedMonster.name}を手札に加えた`, 'info');

              // 残りを並び替えて戻す
              if (remainingCards.length > 1) {
                setPendingDeckReview!({
                  cards: remainingCards,
                  title: '星翼の飛舟エクラオン',
                  message: '残りのカードの順番を変更してデッキに戻してください',
                  allowReorder: true,
                  onConfirm: (reorderedCards: CardWithId[]) => {
                    setMyDeck((prev) => [...reorderedCards, ...prev.slice(cardCount)] as CardWithId[]);
                    addLog(`残りをデッキに戻した: ${reorderedCards.map((c: CardWithId) => c.name).join(' → ')}`, 'info');
                  },
                } as any);
              } else if (remainingCards.length === 1) {
                setMyDeck((prev) => [remainingCards[0], ...prev.slice(cardCount)]);
                addLog(`${remainingCards[0].name}をデッキに戻した`, 'info');
              } else {
                setMyDeck((prev) => prev.slice(cardCount));
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_LEAVE_FIELD,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '場を離れる時、相手に600ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { setOpponentLife } = getPlayerContext(context);

        // 相手にダメージ
        setOpponentLife((prev) => Math.max(0, prev - 600));
        addLog('星翼の飛舟エクラオンの効果: 相手に600ダメージ！', 'damage');
      },
    },
  ],

  /**
   * C0000266: エクラシアの時空炉 (フェイズカード)
   * 初期効果:【発動時】自分のデッキ上1枚を見て、それが「未来属性」カードなら手札に、違えば墓地に送る。
   */
  C0000266: [
    {
      type: TRIGGER_TYPES.ON_PHASE_CARD_ACTIVATE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '発動時: デッキ上1枚を見て、未来属性なら手札に、違えば墓地に送る',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myDeck, setMyDeck, setMyHand, setMyGraveyard } = getPlayerContext(context);

        if (myDeck.length === 0) {
          addLog('デッキにカードがありません', 'info');
          return;
        }

        // デッキトップ1枚を確認
        const topCard = myDeck[0];
        addLog(`エクラシアの時空炉の効果: デッキトップを確認 - ${topCard.name}`, 'info');

        if (topCard.attribute === '未来') {
          // 未来属性なら手札に加える
          setMyDeck((prev) => prev.slice(1));
          setMyHand((prev) => [...prev, topCard]);
          addLog(`${topCard.name}は未来属性！手札に加えた`, 'info');
        } else {
          // 未来属性でなければ墓地に送る
          setMyDeck((prev) => prev.slice(1));
          setMyGraveyard((prev) => [...prev, topCard]);
          addLog(`${topCard.name}は未来属性ではない。墓地に送った`, 'info');
        }
      },
    },
  ],

  /**
   * C0000272: 虚蝕の崩姫エクラリア【禁忌カード】
   * 【自壊時】自分のライフを2500減らす。
   */
  C0000272: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自壊時: 自分のライフを2500減らす',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { setMyLife } = getPlayerContext(context);

        addLog('虚蝕の崩姫エクラリアが破壊された！自分のライフに2500ダメージ！', 'damage');
        setMyLife((prev) => Math.max(0, prev - 2500));
      },
    },
  ],

  /**
   * C0000280: 星灯の少女エクラリア
   * 【自分メインフェイズ時】1ターンに1度、自分のモンスターが場に召喚されたとき、
   * 自分のデッキ上1枚を見て、それが「未来属性」カードならレスト状態のSPトークン１つをアクティブにする。
   */
  C0000280: [
    {
      type: TRIGGER_TYPES.ON_ATTRIBUTE_SUMMON_SELF,  // 自分の召喚時に発動
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自分のモンスター召喚時: デッキ上1枚が未来属性ならSPアクティブ',
      effect: (context: TriggerContext) => {
        const { addLog, card } = context;
        const { myDeck, setMyDeck, myRestedSP, setMyRestedSP, setMyActiveSP, myField } = getPlayerContext(context);

        // このカードがフィールドにいるか確認
        const thisCardOnField = myField.find(m => m && m.id === 'C0000280');
        if (!thisCardOnField) return;

        // 1ターンに1度チェック
        const thisCardAny = thisCardOnField as any;
        if (thisCardAny.usedMainPhaseTriggerC0000280) {
          return;
        }

        // デッキが空なら何もしない
        if (myDeck.length === 0) {
          addLog('星灯の少女エクラリア: デッキにカードがありません', 'info');
          return;
        }

        // デッキトップ1枚を確認
        const topCard = myDeck[0];
        addLog(`星灯の少女エクラリアの効果: デッキトップを確認 - ${topCard.name}`, 'info');

        if (topCard.attribute === '未来') {
          // 未来属性ならレストSPをアクティブに
          if (myRestedSP > 0) {
            setMyRestedSP((prev) => prev - 1);
            setMyActiveSP((prev) => prev + 1);
            addLog(`${topCard.name}は未来属性！SPトークン1個をアクティブにした`, 'info');
          } else {
            addLog(`${topCard.name}は未来属性！（レスト状態のSPがありません）`, 'info');
          }
        } else {
          addLog(`${topCard.name}は未来属性ではない`, 'info');
        }

        // 使用済みフラグを設定
        // Note: フィールド更新は呼び出し元で行う必要がある場合がある
      },
    },
  ],

  /**
   * C0000274: 星辰の魔導術師
   * 【召喚時】自分のデッキ上2枚を見て、1枚を手札に加え、残りをデッキ下に戻す。
   */
  C0000274: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'デッキ上2枚を見て、1枚を手札に加え、残りをデッキ下に戻す',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview } = context;
        const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

        if (myDeck.length === 0) {
          addLog('デッキにカードがありません', 'info');
          return;
        }

        // デッキ上2枚を確認
        const cardCount = Math.min(2, myDeck.length);
        const topCards = myDeck.slice(0, cardCount);
        addLog(`星辰の魔導術師の効果: デッキ上${cardCount}枚を確認`, 'info');

        if (cardCount === 1) {
          // 1枚しかない場合、その1枚を自動で手札に
          setMyHand((prev) => [...prev, topCards[0]]);
          setMyDeck((prev) => prev.slice(1));
          addLog(`${topCards[0].name}を手札に加えた`, 'info');
        } else {
          // 2枚ある場合、プレイヤーに選択させる
          setPendingDeckReview!({
            cards: topCards,
            title: '星辰の魔導術師',
            message: '手札に加えるカードを1枚選択してください（残りはデッキ下へ）',
            allowReorder: false,
            selectMode: { enabled: true, count: 1 },
            onSelect: (selectedCards: CardWithId[], remainingCards: CardWithId[]) => {
              // DeckReviewModalは常に配列を渡すため、配列の最初の要素を取得
              const cardToHand = selectedCards[0];
              const cardToBottom = remainingCards[0];

              setMyHand((prev) => [...prev, cardToHand]);
              setMyDeck((prev) => {
                const deckWithoutTop = prev.slice(cardCount);
                return [...deckWithoutTop, cardToBottom];
              });
              addLog(`${cardToHand.name}を手札に加え、${cardToBottom.name}をデッキの下に戻した`, 'info');
            },
            selectionOwner: context.currentPlayer,
          } as any);
        }
      },
    },
  ],

  /**
   * C0000271: 黄金の時姫エクラリア
   * 【召喚時】自分のデッキ上5枚を見て、コスト6以下のモンスター1枚を場に召喚、残りを好きな順番で戻す。
   * 【自分エンドフェイズ時】上級技使用後、このカード以外のモンスターをすべて墓地に送る。
   */
  C0000271: [
    // 召喚時効果はcardEffects/future.jsで実装
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '上級技発動後：このカード以外のモンスターを墓地に送る',
      effect: (context: TriggerContext) => {
        const { addLog, monsterIndex, card } = context;
        const { myField, setMyField, setMyGraveyard } = getPlayerContext(context);

        // このカードがpendingEndTurnSacrificeフラグを持っているか確認
        const thisCard = (monsterIndex !== undefined ? myField[monsterIndex] : null) ||
          myField.find(m => m && m.id === 'C0000271' && (m as any).pendingEndTurnSacrifice);

        if (!thisCard || !(thisCard as any).pendingEndTurnSacrifice) {
          // 上級技が発動されていない場合はスキップ
          return;
        }

        addLog('黄金の時姫エクラリアの上級技効果が発動！', 'damage');

        // このカード以外のモンスターを墓地に送る
        const sacrificedMonsters: MonsterInstance[] = [];
        const eclariaIndex = myField.findIndex(m => m && m.id === 'C0000271' && (m as any).pendingEndTurnSacrifice);

        setMyField(prev => prev.map((monster, idx) => {
          if (monster && idx !== eclariaIndex) {
            sacrificedMonsters.push(monster);
            addLog(`${monster.name}は墓地に送られた`, 'damage');
            return null;
          }
          if (idx === eclariaIndex && monster) {
            // フラグをリセット
            return { ...monster, pendingEndTurnSacrifice: false } as any;
          }
          return monster;
        }));

        // 墓地に追加
        if (sacrificedMonsters.length > 0) {
          setMyGraveyard(prev => [...prev, ...sacrificedMonsters]);
        } else {
          addLog('他にモンスターがいませんでした', 'info');
        }
      },
    },
  ],

  /**
   * C0000258: 禁忌の鴉王ミラン
   * 【召喚時】このカード以外の場にいる全てのモンスターの効果をターン終了時まで無効化し、相手モンスター全体に1000ダメージを与える。
   * 【自分の「未来」モンスターが破壊された時】相手プレイヤーに800ダメージ。
   * 【自壊時】自分のライフを2000減らす。
   */
  C0000258: [
    // 召喚時トリガー
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '全モンスターの効果を無効化し、相手モンスター全体に1000ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog, monsterIndex } = context;
        const { setMyField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        addLog('禁忌の鴉王ミランが召喚された！', 'info');

        // このカード以外の全モンスターにSILENCE状態異常を付与（ターン終了時まで）
        // 自分のフィールド（このカード以外）
        setMyField((prev) => {
          return prev.map((monster, idx) => {
            if (monster && idx !== monsterIndex) {
              statusEffectEngine.applyStatus(monster, STATUS_EFFECT_TYPES.SILENCE, {
                duration: 0, // このターン終了時まで
                source: 'C0000258',
                sourceName: '禁忌の鴉王ミラン',
              });
              addLog(`${monster.name}の効果がターン終了時まで無効化された`, 'info');
              return { ...monster };
            }
            return monster;
          });
        });

        // 相手のフィールド
        applyStatusToAllOpponentMonsters(context as any, STATUS_EFFECT_TYPES.SILENCE, {
          duration: 0, // このターン終了時まで
        }, '禁忌の鴉王ミラン');

        // 相手モンスター全体に1000ダメージ
        const destroyedMonsters: MonsterInstance[] = [];
        setOpponentField((prev) => {
          return prev.map((monster) => {
            if (monster) {
              const newHp = monster.currentHp - 1000;
              addLog(`${monster.name}に1000ダメージ！（残りHP: ${Math.max(0, newHp)}）`, 'damage');
              if (newHp <= 0) {
                destroyedMonsters.push(monster);
                addLog(`${monster.name}は破壊された！`, 'damage');
                return null;
              }
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        // 破壊されたモンスターを墓地に送る
        if (destroyedMonsters.length > 0) {
          setOpponentGraveyard((prev) => [...prev, ...destroyedMonsters]);
        }
      },
    },
    // 自分の「未来」モンスターが破壊された時トリガー
    {
      type: TRIGGER_TYPES.ON_SELF_MONSTER_DESTROYED,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      displayDescription: '自分の「未来」モンスターが破壊された時',
      description: '相手プレイヤーに800ダメージ',
      condition: (context: TriggerContext) => {
        // 破壊されたカードが未来属性かどうかをチェック
        const destroyedCard = context.destroyedCard;
        return destroyedCard && destroyedCard.attribute === '未来';
      },
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const destroyedCard = context.destroyedCard;

        addLog(`禁忌の鴉王ミランの効果発動！（${destroyedCard?.name || '未来モンスター'}が破壊されたため）`, 'info');
        conditionalDamage(context as any, 800, 'opponent');
      },
    },
    // 自壊時トリガー
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自分のライフを2000減らす',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { setMyLife } = getPlayerContext(context);

        addLog('禁忌の鴉王ミランが破壊された！自分のライフに2000ダメージ！', 'damage');
        setMyLife((prev) => Math.max(0, prev - 2000));
      },
    },
  ],

  /**
   * C0000186: 時空偽造者・ギズネック
   * 【召喚時】相手の手札を1枚ランダムに選び、次のターン終了時までそのカードを使用不能にする。
   */
  C0000186: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手手札1枚を次のターン終了時まで使用不能に',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentHand, setOpponentHand } = getPlayerContext(context);

        if (opponentHand.length === 0) {
          addLog('時空偽造者・ギズネック: 相手の手札がありません', 'info');
          return;
        }

        // ランダムに1枚選択
        const randomIndex = Math.floor(Math.random() * opponentHand.length);

        // 使用不能フラグを付与（次のターン終了時まで = 2ターン持続）
        setOpponentHand((prev) => {
          return prev.map((card, idx) => {
            if (idx === randomIndex) {
              addLog(`時空偽造者・ギズネック: 相手の${card.name}を使用不能に！`, 'info');
              return {
                ...card,
                disabled: true,
                disabledTurnsRemaining: 2, // 次のターン終了時まで
              };
            }
            return card;
          });
        });
      },
    },
  ],

  /**
   * C0000268: 虚蝕の魔導兵
   * 【召喚時】相手のデッキ上2枚を墓地に送り、その中のモンスター1体につき300ダメージ。
   */
  C0000268: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手のデッキ上2枚を墓地に送り、モンスター1体につき300ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;

        // 相手のデッキ上2枚を墓地に送る
        const milledCards = millOpponentDeck(context, 2);

        if (milledCards.length === 0) {
          addLog('相手のデッキにカードがありませんでした', 'info');
          return;
        }

        // 墓地に送ったカードを表示
        addLog(`相手のデッキから${milledCards.length}枚を墓地に送った: ${milledCards.map(c => c.name).join(', ')}`, 'info');

        // 墓地に送ったカードの中のモンスター数をカウント
        const monsterCount = milledCards.filter(card => card.type === 'monster').length;

        if (monsterCount > 0) {
          const totalDamage = monsterCount * 300;
          conditionalDamage(context as any, totalDamage, 'opponent');
          addLog(`モンスター${monsterCount}体分、${totalDamage}ダメージ！`, 'damage');
        } else {
          addLog('墓地に送ったカードにモンスターはいませんでした', 'info');
        }
      },
    },
  ],

  /**
   * C0000316: 未来鴉の預言者
   * 【自分ターン開始時】相手のデッキの上から1枚を見てデッキの上か下に置く。
   */
  C0000316: [
    {
      type: TRIGGER_TYPES.ON_TURN_START_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'ターン開始時: 相手デッキトップを確認して上か下に置く',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview } = context;
        const { opponentDeck, setOpponentDeck } = getPlayerContext(context);

        if (opponentDeck.length === 0) {
          addLog('未来鴉の預言者の効果: 相手のデッキが空です', 'info');
          return;
        }

        const topCard = opponentDeck[0];
        addLog(`未来鴉の預言者の効果: 相手のデッキトップを確認 - ${topCard.name}`, 'info');

        // デッキレビューUIで上か下を選択
        if (setPendingDeckReview) {
          setPendingDeckReview!({
            cards: [topCard],
            message: '相手のデッキトップを確認。上に置くか下に置くか選択',
            selectCount: 0, // 選択不要、表示のみ
            showActions: true, // 上/下ボタン表示
            onTop: () => {
              // 上に置く（そのまま）
              addLog(`${topCard.name}をデッキの上に置いた`, 'info');
            },
            onBottom: () => {
              // 下に置く
              setOpponentDeck(prev => {
                const [first, ...rest] = prev;
                return [...rest, first];
              });
              addLog(`${topCard.name}をデッキの下に置いた`, 'info');
            },
            selectionOwner: context.currentPlayer,
          } as any);
        }
      },
    },
  ],

  /**
   * C0000270: ヴォイドゲートの遺構
   * 【エンドフェイズ時】自分のデッキの上から1枚を墓地に送り、
   * それが「未来属性」カードなら相手モンスター1体の攻撃力を500ダウン
   */
  C0000270: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'デッキトップ1枚を墓地送り、未来属性なら相手ATK-500',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingMonsterTarget } = context;
        const { myDeck, setMyDeck, myGraveyard, setMyGraveyard, opponentField } = getPlayerContext(context);

        // デッキが空の場合
        if (!myDeck || myDeck.length === 0) {
          addLog('デッキにカードがありません', 'info');
          return;
        }

        // デッキトップ1枚を墓地に送る
        const milledCard = myDeck[0];
        const newDeck = myDeck.slice(1);
        setMyDeck(newDeck);
        setMyGraveyard([...myGraveyard, milledCard]);

        addLog(`ヴォイドゲートの遺構: 「${milledCard.name}」を墓地に送った`, 'info');

        // 未来属性カードかチェック
        if (milledCard.attribute === '未来') {
          addLog(`「${milledCard.name}」は未来属性！相手モンスター1体のATK-500`, 'info');

          // 相手フィールドにモンスターがいるか確認
          const validTargets = opponentField
            .map((m, idx) => ({ monster: m, index: idx }))
            .filter(({ monster }) => monster !== null);

          if (validTargets.length === 0) {
            addLog('相手フィールドにモンスターがいません', 'info');
            return;
          }

          // モンスターが1体のみの場合は自動選択
          if (validTargets.length === 1) {
            const targetIdx = validTargets[0].index;
            modifyAttack(context as any, -500, targetIdx, true);
            addLog(`${opponentField[targetIdx]?.name || 'モンスター'}のATKが500ダウン！`, 'info');
            return;
          }

          // 複数いる場合は選択
          setPendingMonsterTarget!({
            message: 'ATKを500ダウンさせる相手モンスターを選択',
            targetPlayer: 'opponent',
            callback: (selectedIndex: number) => {
              modifyAttack(context as any, -500, selectedIndex, true);
              addLog(`${opponentField[selectedIndex]?.name || 'モンスター'}のATKが500ダウン！`, 'info');
            },
            selectionOwner: context.currentPlayer,
          } as any);
        } else {
          addLog(`「${milledCard.name}」は未来属性ではありません`, 'info');
        }
      },
    },
  ],

  /**
   * C0000260: 預言者モナビン
   * 【召喚時】次の自分のターン開始時まで相手の手札を公開。
   */
  C0000260: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時：相手手札公開',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentHand } = getPlayerContext(context);

        addLog('預言者モナビンの召喚時効果発動！', 'info');
        addLog('次の自分のターン開始時まで相手の手札を公開！', 'info');

        // 相手の手札を表示
        if (opponentHand && opponentHand.length > 0) {
          const cardNames = opponentHand.map(c => c.name).join(', ');
          addLog(`相手の手札: ${cardNames}`, 'info');
        } else {
          addLog('相手の手札は0枚です', 'info');
        }

        // 手札公開状態フラグは本来ゲーム状態で管理が必要
        // ここでは現在の手札を表示するのみ
      },
    },
  ],

  /**
   * C0000263: 未来の分岐
   * 【自分のターン開始時】自分のデッキ上3枚を見てコスト5以下の未来属性モンスター1体を場に召喚（コスト無視）、残りを墓地に送る。
   * 【自分の『未来』モンスターが破壊される時】自分に300ダメージ。
   */
  C0000263: [
    {
      type: TRIGGER_TYPES.ON_TURN_START_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'ターン開始時：デッキ上3枚から未来モンスター召喚',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview, currentPlayer, summoningSickness = true } = context;
        const { myDeck, setMyDeck, myField, setMyField, myGraveyard, setMyGraveyard } = getPlayerContext(context);

        if (!myDeck || myDeck.length === 0) {
          addLog('未来の分岐: デッキにカードがありません', 'info');
          return;
        }

        // 空きスロットを確認
        const emptySlot = myField.findIndex(s => s === null);
        if (emptySlot === -1) {
          addLog('未来の分岐: 場が満杯で召喚できません', 'info');
          return;
        }

        // デッキ上3枚を見る
        const top3 = myDeck.slice(0, Math.min(3, myDeck.length));
        addLog(`未来の分岐: デッキ上${top3.length}枚を確認`, 'info');

        // コスト5以下の未来属性モンスターを探す
        const validMonsters = top3.filter(c =>
          c.type === 'monster' && c.attribute === '未来' && c.cost <= 5
        );

        const processCards = (selectedCard: CardWithId | null) => {
          // デッキから上3枚を除去
          setMyDeck(prev => prev.slice(Math.min(3, prev.length)));

          if (selectedCard) {
            // 選んだカードを召喚
            setMyField(prev => {
              const newField = [...prev];
              const slot = newField.findIndex(s => s === null);
              if (slot !== -1) {
                newField[slot] = {
                  ...selectedCard,
                  uniqueId: `${selectedCard.id}_summoned_${Date.now()}`,
                  currentAttack: selectedCard.attack ?? 0,
                  currentHp: selectedCard.hp ?? 0,
                  canAttack: !summoningSickness, // 召喚酔い設定に依存
                  charges: [],
                  statusEffects: [],
                  owner: currentPlayer,
                } as any;
              }
              return newField;
            });
            addLog(`未来の分岐: 「${selectedCard.name}」を召喚！`, 'info');

            // 残りを墓地に
            const rest = top3.filter(c => c.uniqueId !== selectedCard.uniqueId);
            if (rest.length > 0) {
              setMyGraveyard(prev => [...prev, ...rest]);
              addLog(`未来の分岐: ${rest.map(c => c.name).join(', ')}を墓地に送った`, 'info');
            }
          } else {
            // 召喚できるカードがない場合、全て墓地へ
            setMyGraveyard(prev => [...prev, ...top3]);
            addLog(`未来の分岐: ${top3.map(c => c.name).join(', ')}を墓地に送った`, 'info');
          }
        };

        if (validMonsters.length === 0) {
          // 召喚できるモンスターがいない
          processCards(null);
          return;
        }

        if (validMonsters.length === 1) {
          // 1体のみなら自動選択
          processCards(validMonsters[0]);
          return;
        }

        // 複数いる場合は選択UI
        if (setPendingDeckReview) {
          setPendingDeckReview!({
            cards: validMonsters,
            message: '召喚するコスト5以下の未来属性モンスターを選択',
            count: 1,
            callback: (selected: CardWithId[]) => {
              if (selected && selected.length > 0) {
                processCards(selected[0]);
              } else {
                processCards(validMonsters[0]);
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        } else {
          processCards(validMonsters[0]);
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_CATEGORY_MONSTER_DESTROYED,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '未来モンスター破壊時：自分に300ダメージ',
      condition: (context: TriggerContext) => {
        // 破壊されたのが自分の未来属性モンスターか確認
        const { destroyedCard } = context;
        return destroyedCard && destroyedCard.attribute === '未来';
      },
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        conditionalDamage(context as any, 300, 'self');
        addLog('未来の分岐: 未来属性モンスター破壊により自分に300ダメージ！', 'damage');
      },
    },
  ],

  /**
   * C0000265: 鴉の幻影
   * 【自壊時】デッキから『未来』属性モンスター1体を特殊召喚（コスト2以下）。
   * そのモンスターの攻撃力をターン終了時まで倍にする。
   */
  C0000265: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自壊時：コスト2以下未来モンスター召喚、ATK倍',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview, currentPlayer, summoningSickness = true } = context;
        const { myDeck, setMyDeck, myField, setMyField } = getPlayerContext(context);

        // 空きスロットを確認
        const emptySlot = myField.findIndex(s => s === null);
        if (emptySlot === -1) {
          addLog('鴉の幻影: 場が満杯で召喚できません', 'info');
          return;
        }

        // デッキからコスト2以下の未来モンスターを検索
        const validCards = myDeck.filter(c =>
          c.type === 'monster' && c.attribute === '未来' && c.cost <= 2
        );

        if (validCards.length === 0) {
          addLog('鴉の幻影: デッキにコスト2以下の未来属性モンスターがいません', 'info');
          return;
        }

        const summonMonster = (selectedCard: CardWithId) => {
          // デッキから除去
          setMyDeck(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));

          // 召喚（攻撃力2倍）
          const doubledAttack = (selectedCard.attack ?? 0) * 2;
          setMyField(prev => {
            const newField = [...prev];
            const slot = newField.findIndex(s => s === null);
            if (slot !== -1) {
              newField[slot] = {
                ...selectedCard,
                uniqueId: `${selectedCard.id}_summoned_${Date.now()}`,
                currentAttack: doubledAttack,
                currentHp: selectedCard.hp ?? 0,
                canAttack: !summoningSickness, // 召喚酔い設定に依存
                charges: [],
                statusEffects: [],
                owner: currentPlayer,
                atkBuffedUntilEndOfTurn: true,
              } as any;
            }
            return newField;
          });
          addLog(`鴉の幻影: 「${selectedCard.name}」を召喚！攻撃力2倍（${doubledAttack}）！`, 'info');
        };

        if (validCards.length === 1) {
          summonMonster(validCards[0]);
          return;
        }

        // 複数いる場合は選択UI
        if (setPendingDeckReview) {
          setPendingDeckReview!({
            cards: validCards,
            message: '召喚するコスト2以下の未来属性モンスターを選択',
            count: 1,
            callback: (selected: CardWithId[]) => {
              if (selected && selected.length > 0) {
                summonMonster(selected[0]);
              } else {
                summonMonster(validCards[0]);
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        } else {
          summonMonster(validCards[0]);
        }
      },
    },
  ],

  /**
   * C0000360: ヴォランティス・テンペスト
   * 【召喚時】自分のデッキの上から5枚を見て、1枚を手札に加え、残りを好きな順番でデッキに戻す。
   */
  C0000360: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時：デッキ上5枚を見て1枚を手札に',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview } = context;
        const { myDeck, setMyDeck, myHand, setMyHand } = getPlayerContext(context);

        if (!myDeck || myDeck.length === 0) {
          addLog('ヴォランティス・テンペスト: デッキにカードがありません', 'info');
          return;
        }

        // デッキ上5枚を見る
        const top5 = myDeck.slice(0, Math.min(5, myDeck.length));
        addLog(`ヴォランティス・テンペストの召喚時効果発動！デッキ上${top5.length}枚を確認`, 'info');

        const processSelection = (selectedCard: CardWithId | null) => {
          if (!selectedCard) {
            addLog('ヴォランティス・テンペスト: カードが選択されませんでした', 'info');
            return;
          }

          // 選んだカードを手札に加える
          setMyHand(prev => [...prev, selectedCard]);
          addLog(`ヴォランティス・テンペスト: 「${selectedCard.name}」を手札に加えた！`, 'info');

          // 残りをデッキに戻す（デッキトップに）
          const remaining = top5.filter(c => c.uniqueId !== selectedCard.uniqueId);
          setMyDeck(prev => {
            // 元の5枚を除去して、残りのカードをトップに戻す
            const deckWithout5 = prev.slice(Math.min(5, prev.length));
            return [...remaining, ...deckWithout5];
          });

          if (remaining.length > 0) {
            addLog(`残りの${remaining.length}枚をデッキの上に戻した`, 'info');
          }
        };

        // 選択UI表示
        if (setPendingDeckReview && top5.length > 1) {
          setPendingDeckReview!({
            cards: top5,
            message: '手札に加えるカードを1枚選択（残りはデッキの上に戻す）',
            count: 1,
            callback: (selected: CardWithId[]) => {
              if (selected && selected.length > 0) {
                processSelection(selected[0]);
              } else {
                // キャンセル時は最初のカードを選択
                processSelection(top5[0]);
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        } else if (top5.length === 1) {
          // 1枚しかない場合は自動選択
          processSelection(top5[0]);
        }
      },
    },
  ],

  /**
   * C0000400: 呪術狩りの技師ヴァルク
   * 【召喚時】デッキから《呪術狩り》カード1枚をサーチ
   */
  C0000400: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時：《呪術狩り》カード1枚をサーチ',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview } = context;
        const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

        const validCards = myDeck.filter(card =>
          card.name && card.name.includes('呪術狩り')
        );

        if (validCards.length === 0) {
          addLog('呪術狩りの技師ヴァルク【召喚時】: デッキに《呪術狩り》カードがありません', 'info');
          return;
        }

        addLog('呪術狩りの技師ヴァルクの召喚時効果発動！', 'info');

        if (validCards.length === 1) {
          const card = validCards[0];
          setMyDeck(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
          setMyHand(prev => [...prev, card]);
          addLog(`${card.name}を手札に加えた！`, 'info');
          return;
        }

        if (setPendingDeckReview) {
          setPendingDeckReview!({
            message: '手札に加える《呪術狩り》カードを選択',
            cards: validCards,
            count: 1,
            callback: (selectedCards: CardWithId[]) => {
              if (selectedCards && selectedCards.length > 0) {
                const card = selectedCards[0];
                setMyDeck(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
                setMyHand(prev => [...prev, card] as CardWithId[]);
                addLog(`${card.name}を手札に加えた！`, 'info');
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        }
      },
    },
  ],

  /**
   * C0000419: 毒蛾モルフィグナ
   * 【召喚時】相手プレイヤーに400ダメージを与える。
   */
  C0000419: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手に400ダメージ',
      effect: (context: TriggerContext) => {
        context.addLog('毒蛾モルフィグナの効果: 相手プレイヤーに400ダメージ！', 'damage');
        conditionalDamage(context as any, 400, 'opponent');
      },
    },
  ],

  // ========================================
  // ガーディアンモンスター
  // ========================================

  /**
   * C0000439: タイムシールド・ガーディアン
   * 【召喚時】このカードに「守護（1度だけ受けるダメージ半減）」を付与。
   */
  C0000439: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 自身に守護を付与',
      effect: (context: TriggerContext) => {
        const { addLog, slotIndex, monsterIndex } = context;
        const { myField, setMyField } = getPlayerContext(context);

        // このモンスターのスロットを特定
        const targetIndex = slotIndex !== undefined ? slotIndex : monsterIndex;
        if (targetIndex === undefined || !myField[targetIndex]) {
          return;
        }

        // 守護状態を付与（GUARD: 1度だけダメージ半減）
        setMyField(prev => {
          const newField = [...prev];
          if (newField[targetIndex]) {
            const monster = newField[targetIndex];
            const newEffects = [...(monster.statusEffects || [])];
            newEffects.push({
              type: 'GUARD',
              duration: -1, // 使用されるまで継続
              source: 'C0000439',
              sourceName: 'タイムシールド・ガーディアン',
            });
            newField[targetIndex] = {
              ...monster,
              statusEffects: newEffects,
            };
          }
          return newField;
        });

        addLog('タイムシールド・ガーディアンの効果: 守護を獲得！（1度だけダメージ半減）', 'info');
      },
    },
  ],

  /**
   * C0000452: 殺戮の機械人形マーガリン
   * 【召喚時】相手フィールドまたはフェイズカード1枚を破壊する。
   */
  C0000452: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手フィールドまたはフェイズカードを1枚破壊',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingMonsterTarget } = context;
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        // 相手の特殊カード（フィールドカード、フェイズカード）を確認
        // Note: フィールドカードは opponentFieldCard、フェイズカードは opponentPhaseCard として管理されている可能性
        // ここでは相手フィールド上のモンスターを破壊する実装
        // TODO: フィールドカード/フェイズカード破壊のための追加context参照が必要

        const validTargets = opponentField
          .map((m, idx) => ({ monster: m, index: idx }))
          .filter(({ monster }) => monster !== null);

        if (validTargets.length === 0) {
          addLog('殺戮の機械人形マーガリン: 相手フィールドに破壊対象がありません', 'info');
          return;
        }

        const destroyTarget = (targetIndex: number) => {
          const target = opponentField[targetIndex];
          if (target) {
            setOpponentField(prev => {
              const newField = [...prev];
              newField[targetIndex] = null;
              return newField;
            });
            setOpponentGraveyard(prev => [...prev, target]);
            addLog(`殺戮の機械人形マーガリンの効果: ${target.name}を破壊した！`, 'damage');
          }
        };

        // 1体のみの場合は自動選択
        if (validTargets.length === 1) {
          destroyTarget(validTargets[0].index);
          return;
        }

        // 複数いる場合は選択UI
        if (setPendingMonsterTarget) {
          setPendingMonsterTarget!({
            message: '破壊する相手のカードを選択',
            targetPlayer: 'opponent',
            callback: (selectedIndex: number) => {
              destroyTarget(selectedIndex);
            },
            selectionOwner: context.currentPlayer,
          } as any);
        } else {
          destroyTarget(validTargets[0].index);
        }
      },
    },
  ],

  /**
   * C0000547: 対空砲台・フラックタレット
   * 【自分ターン終了時】相手の場に飛行モンスターがいれば、そのうち1体に1000ダメージ。
   */
  C0000547: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンド時: 相手の飛行モンスター1体に1000ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingMonsterTarget } = context;
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);
        const { hasHikou } = require('../keywordAbilities');

        // 相手の飛行モンスターを探す
        const flyingMonsters = opponentField
          .map((m, idx) => ({ monster: m, index: idx }))
          .filter(({ monster }) => monster && hasHikou(monster));

        if (flyingMonsters.length === 0) {
          addLog('対空砲台・フラックタレット: 相手の場に飛行モンスターがいません', 'info');
          return;
        }

        const dealDamage = (targetIndex: number) => {
          const target = opponentField[targetIndex];
          if (!target) return;

          const newHp = target.currentHp - 1000;
          addLog(`対空砲台・フラックタレットの効果: ${target.name}に1000ダメージ！`, 'damage');

          if (newHp <= 0) {
            setOpponentField((prev) => {
              const newField = [...prev];
              newField[targetIndex] = null;
              return newField;
            });
            setOpponentGraveyard((prev) => [...prev, target]);
            addLog(`${target.name}は破壊された！`, 'damage');
          } else {
            setOpponentField((prev) => {
              const newField = [...prev];
              newField[targetIndex] = { ...target, currentHp: newHp };
              return newField;
            });
          }
        };

        // 1体のみの場合は自動選択
        if (flyingMonsters.length === 1) {
          dealDamage(flyingMonsters[0].index);
          return;
        }

        // 複数いる場合は選択
        if (setPendingMonsterTarget) {
          setPendingMonsterTarget!({
            message: '1000ダメージを与える飛行モンスターを選択',
            targetPlayer: 'opponent',
            validTargets: flyingMonsters.map(({ index }) => index),
            callback: (selectedIndex: number) => {
              dealDamage(selectedIndex);
            },
            selectionOwner: context.currentPlayer,
          } as any);
        } else {
          dealDamage(flyingMonsters[0].index);
        }
      },
    },
  ],

  /**
   * C0000561: カーテン職人デンガク
   * 【召喚時】自分の手札に遮光カーテンを生成する。
   */
  C0000561: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 「遮光カーテン」を手札に生成',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { setMyHand } = getPlayerContext(context);

        // 遮光カーテンカードを生成
        const shaderCurtain: CardWithId = {
          id: 'C0000560',
          uniqueId: `C0000560_generated_${Date.now()}_${Math.random()}`,
          name: '遮光カーテン',
          attribute: '闇',
          cost: 1,
          type: 'magic',
          effect: '相手フィールドの光属性モンスター1体を手札に戻す。',
        };

        setMyHand(prev => [...prev, shaderCurtain]);
        addLog('カーテン職人デンガクの効果: 「遮光カーテン」を手札に加えた！', 'info');
      },
    },
  ],
};

/**
 * カードIDから該当するトリガーを取得
 * @param {string} cardId - カードID (例: "C0000085")
 * @returns {Array|null} トリガー配列、または null
 */
export const getFutureCardTriggers = (cardId: string): TriggerDefinition[] | null => {
  const triggers = futureCardTriggers as unknown as Record<string, TriggerDefinition[]>;
  return triggers[cardId] || null;
};

/**
 * 未来属性カードのトリガーが実装されているか確認
 * @param {string} cardId - カードID
 * @returns {boolean}
 */
export const hasFutureCardTrigger = (cardId: string): boolean => {
  return cardId in futureCardTriggers;
};
